import { preload } from "suspend-react";
import { preloadFont } from "troika-three-text";

// The 4 bundled subsets behind the world-space panel system. Covers ASCII,
// the 64 CJK glyphs actually rendered by this app (grep-verified against
// src/), and the punctuation in live use (· — – …).
//
// Coverage gaps, known and accepted (do NOT assume covered if you add new
// text — verify with fonttools first):
//   - Arrow/diagonal glyphs (← → ↑ ↓ ↖ ↗ ↙ ↘) and guillemets (‹ › ▸ ◂):
//     only reachable from dead code (GHOST_DEMO_ENABLED=false, the
//     "consolidate" tutorial phase is never entered). If that flow is ever
//     revived, these glyphs will tofu — regenerate the subsets first.
//   - ō (macron): Zen Old Mincho / Zen Kaku Gothic New do not carry a real
//     glyph for it (Google's subsetter silently drops the macron and
//     serves bare "o"). The one live occurrence — campaign.ts's phase VIII
//     name "Happō" — is rendered de-macronned ("Happo") in the 3D menu
//     only; the DOM menu (Menu.tsx) keeps the source string, since browser
//     fonts render it fine there.
//
// RULE: any new glyph added to a <PanelText> string must be verified present
// in all 4 files before shipping (fonttools TTFont(...).getBestCmap()), or
// it silently tofus in the headset — this is exactly the adad966 bug.
export const FONT = {
  minchoBlack: "/assets/fonts/ZenOldMincho-Black.subset.woff2",
  minchoBold: "/assets/fonts/ZenOldMincho-Bold.subset.woff2",
  gothicMedium: "/assets/fonts/ZenKakuGothicNew-Medium.subset.woff2",
  gothicBold: "/assets/fonts/ZenKakuGothicNew-Bold.subset.woff2",
} as const;

export type FontUrl = (typeof FONT)[keyof typeof FONT];

const failed = new Set<string>();

// ⚠ KILL SWITCH (2026-07-15): on-device, BOTH MenuScreen3D and PauseScreen
// (the only two screens using a custom `font=`) rendered nothing at all —
// no error, no PanelErrorBoundary fallback, no visible text — while HUD
// (troika's default font, no `font` prop) kept working. That pattern means
// the local <Suspense> is stuck forever (a thrown ERROR would have hit the
// boundary; a stuck PROMISE just leaves the fallback={null} up
// indefinitely), most likely in troika's custom-woff2 loading path —
// something this codebase had never exercised before this redesign (every
// prior <Text> in the project's history used the default font).
//
// Until that's root-caused, fontOrDefault() unconditionally returns
// undefined so every PanelText falls back to the PROVEN-WORKING default —
// this is a full but temporary loss of the Zen Mincho/Gothic typefaces
// (the design/HANDOFF gets deferred, not lost), in exchange for the game
// being playable again. warm()'s preload is disabled to stop touching
// whatever's hanging. Do not re-enable either without a device test.
function warm(font: FontUrl): void {
  fetch(font, { method: "HEAD" })
    .then((res) => {
      if (!res.ok) failed.add(font);
    })
    .catch(() => failed.add(font));

  preload(
    () =>
      new Promise<void>((resolve) => {
        preloadFont({ font, characters: undefined }, () => resolve());
      }),
    ["troika-text", font, undefined],
  );
}

// Disabled — see the kill-switch note above.
// for (const font of Object.values(FONT)) warm(font);
void warm; // keep the function (and its no-unused-vars silence) for when this is re-enabled

// Returns the font URL, or undefined (troika's built-in default) if the
// woff2 failed to fetch — offline-safe degradation to today's behavior
// instead of a blank panel. Import fonts.ts once, early (src/index.tsx),
// so warm() has run before the first <PanelText> mounts.
export function fontOrDefault(_font: FontUrl): string | undefined {
  return undefined; // kill switch — see module doc above
}
